import { z } from 'zod';
// ---------- F18 Hitos de pago ----------
export const MilestoneStatusSchema = z.enum(['PENDIENTE', 'FACTURADO']);
export const MilestoneStatusLabel = {
    PENDIENTE: 'Pendiente',
    FACTURADO: 'Facturado',
};
const decimalString = z
    .union([z.number(), z.string()])
    .transform((v) => (typeof v === 'number' ? v.toString() : v));
export const MilestoneItemInputSchema = z.object({
    position: z.number().int().min(0).default(0),
    title: z.string().min(1).max(300),
    description: z.string().max(2000).nullable().optional(),
    /** Base imponible en céntimos; el IVA se aplica al facturar. */
    amountCents: z.number().int(),
    vatRate: decimalString.default('0.21'),
    dueDate: z.string().datetime(),
});
/** Alta en bloque: un plan de pagos completo de una vez. */
export const CreateMilestonesSchema = z.object({
    clientId: z.string(),
    opportunityId: z.string().nullable().optional(),
    quoteId: z.string().nullable().optional(),
    items: z.array(MilestoneItemInputSchema).min(1).max(50),
});
export const UpdateMilestoneSchema = z.object({
    title: z.string().min(1).max(300).optional(),
    description: z.string().max(2000).nullable().optional(),
    amountCents: z.number().int().optional(),
    vatRate: decimalString.optional(),
    dueDate: z.string().datetime().optional(),
});
/** Facturar una selección de hitos pendientes (mismo cliente). */
export const InvoiceMilestonesSchema = z.object({
    ids: z.array(z.string()).min(1).max(50),
});
/**
 * M6 — Convertir un hito PENDIENTE en cuota recurrente.
 * El hito se retira (soft delete, con su evento de calendario) y nace una
 * RecurringInvoice con una línea equivalente. Sin maxOccurrences ni endsAt
 * la cuota queda "sin fecha límite" → alerta de renovación en las fichas.
 */
export const ConvertMilestoneToRecurringSchema = z.object({
    periodicity: z
        .enum(['MONTHLY', 'BIMONTHLY', 'QUARTERLY', 'SEMIANNUAL', 'YEARLY'])
        .default('MONTHLY'),
    /** Día de emisión (1-28). Default: día del dueDate del hito (cap 28). */
    dayOfMonth: z.coerce.number().int().min(1).max(28).optional(),
    /** Primera cuota (yyyy-mm-dd o ISO). Default: dueDate del hito. */
    startDate: z.string().nullable().optional(),
    maxOccurrences: z.number().int().positive().nullable().optional(),
    endsAt: z.string().nullable().optional(),
});
export const MilestoneDtoSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    clientDisplayName: z.string(),
    opportunityId: z.string().nullable(),
    opportunityCode: z.string().nullable(),
    quoteId: z.string().nullable(),
    quoteNumber: z.string().nullable(),
    position: z.number().int(),
    title: z.string(),
    description: z.string().nullable(),
    amountCents: z.number().int(),
    vatRate: z.string(),
    dueDate: z.string(),
    status: MilestoneStatusSchema,
    invoiceId: z.string().nullable(),
    invoiceNumber: z.string().nullable(),
    /** Estado de la factura vinculada (BORRADOR/EMITIDA/PARCIAL/COBRADA/ANULADA); FACTURADO no implica cobrado. */
    invoiceStatus: z.string().nullable().default(null),
    createdAt: z.string(),
});
export const MilestoneListResponseSchema = z.object({
    items: z.array(MilestoneDtoSchema),
    total: z.number().int(),
    /** Suma de bases pendientes del filtro actual, en céntimos. */
    pendingAmountCents: z.number().int(),
});
// ---------- Pieza 6 — Onboarding asistido: contrato → hitos ----------
/** Petición: sugerir un plan de hitos a partir de un documento (contrato). */
export const SuggestMilestonesSchema = z.object({
    /** Documento contrato del que extraer los hitos (debe ser PDF). */
    documentId: z.string().min(1, 'Selecciona el contrato'),
    /** Importe total del proyecto (base imponible, céntimos) a repartir. */
    dealAmountCents: z.number().int().positive('Indica el importe del proyecto'),
});
/** Un hito sugerido por la IA (no persistido; el usuario lo revisa y aprueba). */
export const SuggestedMilestoneSchema = z.object({
    title: z.string(),
    description: z.string().nullable(),
    /** Porcentaje del total como fracción (0.30 = 30%). */
    pct: z.number(),
    /** Base imponible en céntimos (derivada de pct × total). */
    amountCents: z.number().int(),
    /** Fecha prevista propuesta (ISO) o null si la IA no la infiere. */
    dueDate: z.string().datetime().nullable(),
});
export const SuggestMilestonesResponseSchema = z.object({
    items: z.array(SuggestedMilestoneSchema),
    /** Resumen/avisos de la IA sobre el reparto (p.ej. supuestos). */
    notes: z.string().nullable(),
    /** Suma de pct de los hitos (≈1 si cuadra al 100%). */
    totalPct: z.number(),
});
// ---------- Sugerir LÍNEAS DE FACTURACIÓN (oportunidad) desde la propuesta ----------
// Mientras es solo una oportunidad (no ganada), la IA no propone hitos de proyecto
// sino las partidas FACTURABLES de la propuesta, que rellenan las líneas de servicio.
/** Petición: sugerir líneas de facturación a partir de un documento (propuesta PDF). */
export const SuggestServiceLinesSchema = z.object({
    documentId: z.string().min(1, 'Selecciona la propuesta'),
});
/** Una partida facturable sugerida por la IA (no persistida; el usuario la revisa). */
export const SuggestedServiceLineSchema = z.object({
    /** Concepto/partida tal como aparece en la propuesta. */
    concept: z.string(),
    /** Base imponible en céntimos (sin IVA). */
    amountCents: z.number().int().min(0),
});
export const SuggestServiceLinesResponseSchema = z.object({
    lines: z.array(SuggestedServiceLineSchema),
    /** Resumen/avisos de la IA (p.ej. importes en participación excluidos); null si no aplica. */
    notes: z.string().nullable(),
});
//# sourceMappingURL=milestones.js.map